import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { FLOW_CHROME, selectRetryPathForFlow } from 'features/govtId/lib/flowChrome';
import { resetSessionTimer } from 'store/common/slices/general';
import { selectSessionToken } from 'features/onboarding/state/selectors';
import {
    selectCompany,
    selectProfileError,
    selectProfileLoading,
} from 'store/common/selectors/profile';

/**
 * The paths and resets the shared failure and timeout screens use, plus whether the verification
 * they depend on has loaded.
 */
export const useGovtIdFlowChrome = () => {
    const dispatch = useDispatch();

    const company = useSelector(selectCompany);
    const profileLoading = useSelector(selectProfileLoading);
    const profileError = useSelector(selectProfileError);
    const sessionToken = useSelector(selectSessionToken);

    const retryPath = useSelector(selectRetryPathForFlow);

    // The retry path follows the verification response. A screen entered directly — a refresh, a
    // typed URL — waits for it, so it doesn't point the retry at the wrong step for one paint. A
    // mount with no session token never fetches a profile at all, so it resolves straight away.
    const isFlowResolved =
        !profileLoading && (Boolean(company?.uuid) || Boolean(profileError) || !sessionToken);

    // Drops whatever the flow captured so far, so the next entry starts clean. The error-message
    // reset stays with the callers that already do it — this runs on screens that never touched it.
    const resetCaptureState = useCallback(() => {
        dispatch(resetSessionTimer());
        FLOW_CHROME.resetActions.forEach((resetAction) => dispatch(resetAction()));
    }, [dispatch]);

    return { ...FLOW_CHROME, retryPath, isFlowResolved, resetCaptureState };
};
